import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { downloadsChart } from "./charts";
import { formatNumber } from "../../../../utils/numbers";
import { useStoreDownloads } from "./useDashboardData";

const RANGES = [7, 30, "all"];

// Same counting as the store cards above
const PLATFORMS = [
  { key: "android", name: "Android", icon: "lab la-google-play", color: "#22c58b", metric: "Downloads" },
  { key: "ios", name: "iOS", icon: "lab la-apple", color: "#27aae1", metric: "Downloads" },
];

/** "2026-10-15" → "15 oct.", "2026-10" → "oct. 2026" (UTC, the stores count per UTC day). */
export const periodLabel = (period, locale) => {
  const [year, month, day] = period.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day || 1));
  return date.toLocaleDateString(
    locale,
    day
      ? { day: "numeric", month: "short", timeZone: "UTC" }
      : { month: "short", year: "numeric", timeZone: "UTC" }
  );
};

/** "janv. – sept. 2025": months of `year` up to `until` ("2025-09"). */
export const earlierLabel = (year, until, locale) =>
  `${periodLabel(`${year}-01`, locale).replace(/\s*\d{4}$/, "")} – ${periodLabel(until, locale)}`;

/** Long date of a "YYYY-MM-DD" day, e.g. "15 octobre 2026". */
const longDate = (period, locale) =>
  period
    ? new Date(`${period}T00:00:00Z`).toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

/**
 * Values of both platforms on a common x axis: the union of their periods. Each store
 * publishes its figures with its own delay: outside a store's own range the value is
 * `null` (not published yet, no gap drawn as 0), inside it a missing period counts as 0.
 */
export function alignSeries(data) {
  const periods = [
    ...new Set(PLATFORMS.flatMap(({ key }) => (data?.[key]?.points || []).map((p) => p.period))),
  ].sort();
  const valuesOf = (key) => {
    const points = data?.[key]?.points || [];
    const byPeriod = Object.fromEntries(points.map((p) => [p.period, p.value]));
    const first = points[0]?.period;
    const last = points[points.length - 1]?.period;
    return periods.map((period) =>
      first && period >= first && period <= last ? byPeriod[period] ?? 0 : null
    );
  };
  return { periods, values: Object.fromEntries(PLATFORMS.map(({ key }) => [key, valuesOf(key)])) };
}

/** Days of one store as chart points `{ x: UTC timestamp, y }`. */
export const dailyPoints = (platform) =>
  (platform?.points || []).map((point) => ({
    x: Date.parse(`${point.period}T00:00:00Z`),
    y: point.value,
  }));

/** Downloads of both apps per day (last 7 / 30 days, until yesterday) or per month since launch. */
const StoreDownloads = () => {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState(30);
  const { loading, data } = useStoreDownloads(range);
  // Shape of the data on screen (the previous range stays displayed while the new one loads)
  const monthly = (data?.range ?? range) === "all";

  const { periods, values } = useMemo(() => alignSeries(data), [data]);
  // Rebuilt only when the data changes: ApexCharts redraws (and garbles) on every new options object
  const chart = useMemo(
    () =>
      monthly
        ? downloadsChart(
            // Stacked bars: a month a store has not published counts as 0
            PLATFORMS.map(({ key, name, color }) => ({ name, color, data: values[key].map((value) => value ?? 0) })),
            { monthly, categories: periods.map((period) => periodLabel(period, i18n.language)) }
          )
        : downloadsChart(
            PLATFORMS.map(({ key, name, color }) => ({ name, color, data: dailyPoints(data?.[key]) })),
            { formatDate: (timestamp) => periodLabel(new Date(timestamp).toISOString().slice(0, 10), i18n.language) }
          ),
    [data, monthly, periods, values, i18n.language]
  );
  const hasPoints = periods.length > 0;

  const rangeLabel = (value) =>
    value === "all"
      ? t("Since the beginning")
      : value === 7
      ? t("Last 7 days")
      : t("Last 30 days");

  /** Period of a store's total; notes when the store has not published the last days yet. */
  const periodNote = (platform) => {
    if (!platform?.lastDate) return t("No data yet");
    if (monthly) return t("Since the app was released");
    const range = `${longDate(platform.from, i18n.language)} → ${longDate(platform.to, i18n.language)}`;
    return platform.lastDate < platform.to
      ? `${range} (${t("published until")} ${periodLabel(platform.lastDate, i18n.language)})`
      : range;
  };

  return (
    <section className="dash-card dash-downloads" aria-label={t("Downloads")}>
      <div className="dash-card-header">
        <div>
          <h2 className="dash-card-title">{t("Downloads")}</h2>
          <p className="dash-downloads-subtitle">
            {monthly ? t("Per month, same figures as the store cards") : t("Per day, same figures as the store cards")}
          </p>
        </div>
        <div className="dash-segmented" role="group" aria-label={t("Downloads period")}>
          {RANGES.map((value) => (
            <button
              type="button"
              key={value}
              className={range === value ? "is-active" : ""}
              aria-pressed={range === value}
              onClick={() => setRange(value)}
            >
              {rangeLabel(value)}
            </button>
          ))}
        </div>
      </div>

      <div className="dash-downloads-totals">
        {PLATFORMS.map(({ key, name, icon, color, metric }) => {
          const platform = data?.[key];
          return (
            <div className="dash-downloads-total" key={key} style={{ "--store-color": color }}>
              <span className="dash-downloads-total-name">
                <i className={icon} aria-hidden="true"></i>
                {name} · {t(metric)}
              </span>
              <strong data-testid={`downloads-total-${key}`}>
                {loading && !data ? "…" : platform ? formatNumber(platform.total) : "—"}
              </strong>
              <span className="dash-downloads-total-note">{periodNote(platform)}</span>
            </div>
          );
        })}
      </div>

      {loading && !data ? (
        <div className="dash-skeleton" style={{ height: 300 }} />
      ) : !data ? (
        <p className="dash-empty">{t("Store statistics are unavailable")}</p>
      ) : !hasPoints ? (
        <p className="dash-empty">
          {data.syncing ? t("First synchronisation with the stores in progress…") : t("No data yet")}
        </p>
      ) : (
        <Chart key={chart.type} options={chart.options} series={chart.series} type={chart.type} height={300} />
      )}

      {monthly && data?.ios?.earlier?.length > 0 && (
        <div className="dash-downloads-earlier">
          <span className="dash-downloads-earlier-title">
            <i className="lab la-apple" aria-hidden="true"></i>
            {t("iOS before the monthly detail (Apple keeps monthly reports for 12 months only)")}
          </span>
          <ul>
            {data.ios.earlier.map(({ year, value, until }) => (
              <li key={year}>
                <span>{until ? earlierLabel(year, until, i18n.language) : year}</span>
                <strong>{formatNumber(value)}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && hasPoints && (
        <p className="dash-downloads-notes">
          {t("The stores publish their figures 1 to 3 days late: the last days fill in afterwards.")}
        </p>
      )}
    </section>
  );
};

export default StoreDownloads;
